import _ from 'lodash';

/**
 * Company filter adapters, extracted from GlobalFilterPanel so pages built on the Skylab
 * filters panel can resolve companyId <-> companyCode without pulling in that component.
 *
 * Semantics are a straight lift of GlobalFilterPanel.adaptCompanyId() for the
 * useCompanyCode === false case, which is the only shape these pages use.
 */

export const ALL_COMPANIES = 'All';

/**
 * The filter field names, named here rather than in GlobalFilterPanel so pages on the Skylab
 * filters panel do not have to import that component for a pair of strings. They are also the
 * query parameter and API parameter names, so they are an external contract, not a local choice.
 */
export const COMPANY_ID_FIELD = 'companyId';
export const COMPANY_CODE_FIELD = 'companyCode';

/**
 * Finds a company by an arbitrary key, comparing as strings so a numeric id from the API
 * still matches the string id that arrives from a query parameter.
 */
export function findCompanyBy(companyList = [], key = 'id', value = '') {
    return (companyList || []).find((company) => `${company[key]}` === `${value}`);
}

/**
 * Fills in whichever of companyId / companyCode is missing, and clears companyCode when no
 * specific company is selected.
 *
 * Returns the selection untouched when the company list has not loaded yet - callers are
 * expected to re-run this once the list arrives.
 */
export function resolveCompanySelection(selection = {}, companyList = []) {
    if (_.isEmpty(companyList)) {
        return Object.assign({}, selection);
    }

    const resolved = Object.assign({}, selection);
    const companyId = resolved.companyId;
    const companyCode = resolved.companyCode;

    // On page load we may only know the code (it is the value carried in the URL).
    if (companyId === undefined && !_.isEmpty(companyCode)) {
        const matchedCompany = findCompanyBy(companyList, 'code', companyCode);
        if (matchedCompany) {
            resolved.companyId = matchedCompany.id;
        }
    }
    // No specific company selected, so the previously resolved code no longer applies.
    else if (_.isEmpty(`${companyId}`) || `${companyId}` === ALL_COMPANIES) {
        delete resolved.companyCode;
    }
    else {
        const matchedCompany = findCompanyBy(companyList, 'id', companyId);
        if (matchedCompany) {
            resolved.companyCode = matchedCompany.code;
        }
    }

    return resolved;
}

/**
 * True when the selection carries no specific company, i.e. the 'All' sentinel or nothing at all.
 */
export function isAllCompanies(companyId) {
    return !companyId || `${companyId}` === ALL_COMPANIES;
}
